// Leads API — typed-обёртки над CRUD + convert /api/leads/* (V12 U-34, волна R62).
// Контракт (постановка Lead'а): GET/POST /api/leads, GET/PATCH /api/leads/{id},
// POST /api/leads/{id}/convert; GET list с фильтрами q/fields/status.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { t } from '@/i18n';
import type { SendEmailPayload, SendEmailResponse } from './email';
import type { Page } from './tasks';
import type { Deal } from './deals';

/** Статус лида (backend LeadStatus; KISS-статусы §3.1). */
export type LeadStatus = 'NEW' | 'IN_PROGRESS' | 'CONVERTED' | 'DISCARDED';

/**
 * Источник заявки (backend LeadSource).
 * JIVO — лид из вебхука JivoChat (V23; контент-каналы без ручного создания в CRM-UI).
 */
export type LeadSource = 'LANDING' | 'MANUAL' | 'JIVO';

/** DTO лида (wire-формат backend LeadDto). */
export interface Lead {
  id: string;
  /** Заголовок лида (в UI — «ФИО/Компания»). */
  title: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  /** Название компании ИЗ ЗАЯВКИ (отдельно от companies.name). */
  companyName: string | null;
  /** Пользовательское поле инстанса (research A6): номер лицензии. */
  licenseNumber: string | null;
  /** Пользовательское поле: имя сайта. */
  siteName: string | null;
  /** Пользовательское поле: IP адрес (VARCHAR(45) — IPv4+IPv6). */
  ipAddress: string | null;
  source: LeadSource;
  status: LeadStatus;
  notes: string | null;
  /** FK причины отказа при статусе DISCARDED; null у открытых и легаси-DISCARDED. */
  rejectionReasonId?: string | null;
  /** Имя причины отказа (JOIN каталога); null если причины нет. */
  rejectionReasonName?: string | null;
  /** FK → contacts.id (V23: Jivo-контакт из вебхука); null у лида без связи (additive-optional). */
  contactId?: string | null;
  /** Имя связанного контакта (transient JOIN, V23); null без связи или без имени. */
  contactName?: string | null;
  /** Момент входа в DISCARDED; null у легаси и не-DISCARDED. */
  discardedAt?: string | null;
  /** Создатель лида; NULL для анонимных лендинг-заявок. */
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  /** Soft-delete (канон HR-C7). */
  canceledAt: string | null;
}

export interface LeadFilters {
  page?: number;
  size?: number;
  /** Живой поиск: ILIKE по выбранным полям (≤100 символов, канон R33b). */
  q?: string;
  /** CSV-список полей поиска (SSOT §3.3); отсутствует → все поля (обратная совместимость). */
  fields?: string;
  /** Фильтр по статусу (контракт постановки: одиночный). */
  status?: LeadStatus;
  /** Сортировка «field,direction» (whitelist backend createdAt|updatedAt). */
  sort?: string;
}

export interface LeadCreateRequest {
  title: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  licenseNumber?: string | null;
  siteName?: string | null;
  ipAddress?: string | null;
  notes?: string | null;
  /** CRM-UI шлёт MANUAL (landing-путь — вне RBAC, §3.6). */
  source?: LeadSource;
}

export interface LeadUpdateRequest {
  title?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  licenseNumber?: string | null;
  siteName?: string | null;
  ipAddress?: string | null;
  notes?: string | null;
  status?: LeadStatus;
  /** Обязателен при входе в DISCARDED; иначе игнорируется. */
  rejectionReasonId?: string | null;
  /** Optimistic lock (канон HR-C3/HR-D3). */
  expectedUpdatedAt: string;
}

/** Конверсия лид→сделка (Tech-Story §3.1; departmentId/ownerUserId обязательны). */
export interface LeadConvertRequest {
  /** Заголовок сделки (default = lead.title). */
  title?: string;
  amount?: number | null;
  /** Валюта (54-ФЗ: default RUB на backend). */
  currency?: string;
  departmentId: string;
  ownerUserId: string;
  companyId?: string | null;
  contactId?: string | null;
  /** Плановая дата закрытия "YYYY-MM-DD". */
  dueDate?: string | null;
}

/** Leads API. */
export const leadsApi = {
  /** GET /api/leads?filters → Page<Lead>. */
  async list(filters: LeadFilters = {}): Promise<Page<Lead>> {
    const { data } = await apiClient.get<Page<Lead>>('/api/leads', { params: filters });
    return data;
  },

  /** GET /api/leads/{id} → Lead. */
  async get(id: string): Promise<Lead> {
    const { data } = await apiClient.get<Lead>(`/api/leads/${id}`);
    return data;
  },

  /** POST /api/leads → 201 Lead. */
  async create(payload: LeadCreateRequest): Promise<Lead> {
    const { data } = await apiClient.post<Lead>('/api/leads', payload);
    return data;
  },

  /** PATCH /api/leads/{id} → Lead (optimistic lock через expectedUpdatedAt). */
  async update(id: string, payload: LeadUpdateRequest): Promise<Lead> {
    const { data } = await apiClient.patch<Lead>(`/api/leads/${id}`, payload);
    return data;
  },

  /** POST /api/leads/{id}/convert → 201 Deal (конверсия лид→сделка, U-34-B). */
  async convert(id: string, payload: LeadConvertRequest): Promise<Deal> {
    const { data } = await apiClient.post<Deal>(`/api/leads/${id}/convert`, payload);
    return data;
  },

  /**
   * POST /api/leads/{id}/send-email → 202 SendEmailResponse{deliveryId, status, error?}.
   * Тело {@link SendEmailPayload}: templateId обязателен; accountId/recipientEmail/subject/body опциональны.
   */
  async sendEmail(leadId: string, payload: SendEmailPayload): Promise<SendEmailResponse> {
    const { data } = await apiClient.post<SendEmailResponse>(`/api/leads/${leadId}/send-email`, payload);
    return data;
  },
};

// === Лид-справочник (U-15: «названия, а не id» — привязка платежей к лиду) ===

/** Сколько лидов запрашиваем для построения map (backend size ≤ 100, канон компаний). */
const LEADS_PAGE_SIZE = 100;

/** staleTime для leads-map: справочник лидов меняется редко. */
const LEADS_STALE_TIME_MS = 5 * 60 * 1000;

/** Строит Map id → title из Page<Lead>. */
export function buildLeadMap(page: Page<Lead>): Map<string, string> {
  return new Map(page.content.map((l) => [l.id, l.title]));
}

/**
 * Резолвит id лида в его заголовок (B2-канон: неизвестный id → «Неизвестно», null → «—»).
 * Используется в списке платежей: PaymentDto имён лида НЕ содержит (девиация D-1 backend).
 */
export function resolveLeadTitle(
  leadMap: Map<string, string> | undefined,
  leadId: string | null | undefined,
): string {
  if (!leadId) return '—';
  return leadMap?.get(leadId) ?? t('common.unknown');
}

/** TanStack Query hook: Map<id, title> для резолва заголовков лидов. */
export function useLeadMap(): Map<string, string> | undefined {
  const query = useQuery({
    queryKey: ['leads-map'],
    queryFn: () => leadsApi.list({ page: 0, size: LEADS_PAGE_SIZE }),
    staleTime: LEADS_STALE_TIME_MS,
    select: buildLeadMap,
    retry: false,
  });
  return query.data;
}
